import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import { LoadingButton } from "@mui/lab";
import { useHelpWorkflow } from "context/HelpWorkflowContext";
import { useHelpPersona } from "hooks/useHelpPersona";
import {
  listHelpContentForRole,
  listHelpContentForWorkflow,
  searchHelpContent,
} from "services/helpContentService";
import { getHelpVideo } from "services/helpVideoService";

function openVideoUrl(video) {
  const url = video?.downloadUrl || video?.storageUrl || null;
  if (url) {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

function HelpResultCard({ row, onSelectTopic }) {
  const [video, setVideo] = useState(null);
  const [videoLoading, setVideoLoading] = useState(Boolean(row.linkedVideoId));

  useEffect(() => {
    if (!row.linkedVideoId) {
      setVideoLoading(false);
      return undefined;
    }
    let cancelled = false;
    setVideoLoading(true);
    getHelpVideo(row.linkedVideoId)
      .then((entry) => {
        if (!cancelled) setVideo(entry);
      })
      .finally(() => {
        if (!cancelled) setVideoLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [row.linkedVideoId]);

  const videoUrl = video?.downloadUrl || video?.storageUrl || null;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        p: 2,
      }}
    >
      <Typography variant="subtitle1">{row.title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
        {row.bodyText}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
        {row.linkedVideoId ? (
          <LoadingButton
            size="small"
            loading={videoLoading}
            disabled={!videoUrl}
            startIcon={<OpenInNewRounded />}
            onClick={() => openVideoUrl(video)}
          >
            Watch video
          </LoadingButton>
        ) : null}
        <Button size="small" onClick={() => onSelectTopic(row)}>
          View topic
        </Button>
      </Stack>
    </Box>
  );
}

const HelpChatModal = ({ open, onClose }) => {
  const { workflowId, pageTopics = [] } = useHelpWorkflow();
  const { role, loading: personaLoading } = useHelpPersona();
  const [topics, setTopics] = useState([]);
  const [contextTopics, setContextTopics] = useState([]);
  const [results, setResults] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [searched, setSearched] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);

  const { control, handleSubmit, reset } = useForm({
    defaultValues: { query: "" },
  });

  const loadTopics = useCallback(async () => {
    if (!role) {
      setTopics([]);
      setContextTopics([]);
      return;
    }
    setTopicsLoading(true);
    setLoadError(null);
    try {
      const [allTopics, scoped] = await Promise.all([
        listHelpContentForRole(role),
        listHelpContentForWorkflow(role, workflowId),
      ]);
      setTopics(allTopics);
      setContextTopics(scoped);
    } catch (err) {
      console.warn("[HelpChatModal] topic load failed", err);
      setLoadError("We couldn't load help topics right now.");
      setTopics([]);
      setContextTopics([]);
    } finally {
      setTopicsLoading(false);
    }
  }, [role, workflowId]);

  useEffect(() => {
    if (!open) {
      reset({ query: "" });
      setResults([]);
      setSearched(false);
      setSelectedTopic(null);
      return;
    }
    loadTopics();
  }, [open, loadTopics, reset]);

  const onSubmit = async ({ query }) => {
    if (!role) return;
    setSearchLoading(true);
    setLoadError(null);
    setSearched(true);
    setSelectedTopic(null);
    try {
      const matches = await searchHelpContent(role, query);
      setResults(matches);
    } catch (err) {
      console.warn("[HelpChatModal] search failed", err);
      setLoadError("We couldn't search help content right now.");
      setResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const displayTopics = useMemo(() => {
    if (selectedTopic) return [selectedTopic];
    if (searched) return results;
    if (pageTopics.length) return pageTopics;
    if (contextTopics.length && workflowId) return contextTopics;
    return topics;
  }, [selectedTopic, searched, results, pageTopics, contextTopics, workflowId, topics]);

  const showingPageMenu = pageTopics.length > 0 && !searched && !selectedTopic;

  const showEmptySearch =
    searched && !searchLoading && !selectedTopic && results.length === 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle
        sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", pb: 1 }}
        component="div"
      >
        <Box>
          <Typography variant="h6" component="span">
            Help
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontWeight: 400 }}>
            {pageTopics.length
              ? "Topics for this page are listed below."
              : "Search verified help topics for your role. Answers come from authored content only."}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ ml: 2, mt: 0.25 }} aria-label="Close help">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1, pb: 3 }}>
        {personaLoading && pageTopics.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : !role && pageTopics.length === 0 ? (
          <Alert severity="info">
            Sign in to see role-specific help topics.
          </Alert>
        ) : (
          <Stack spacing={2}>
            {loadError ? (
              <Alert severity="error" onClose={() => setLoadError(null)}>
                {loadError}
              </Alert>
            ) : null}

            {role ? (
              <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="flex-start">
                  <Controller
                    name="query"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="What do you need help with?"
                        placeholder="e.g. promote a candidate, session replay"
                      />
                    )}
                  />
                  <LoadingButton type="submit" variant="contained" loading={searchLoading}>
                    Search
                  </LoadingButton>
                </Stack>
              </Box>
            ) : null}

            {(workflowId || pageTopics.length > 0) && !searched && !selectedTopic ? (
              <Typography variant="caption" color="text.secondary">
                Showing topics for this page when available.
              </Typography>
            ) : null}

            {topicsLoading && pageTopics.length === 0 ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            ) : showEmptySearch ? (
              <Stack spacing={2}>
                <Alert severity="info">No help content found for that yet.</Alert>
                {pageTopics.length ? (
                  <List dense disablePadding>
                    {pageTopics.map((row) => (
                      <ListItem key={row.id || row.contentId} disablePadding>
                        <ListItemButton onClick={() => setSelectedTopic(row)}>
                          <ListItemText primary={row.title} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                ) : null}
                {topics.length ? (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Available topics
                    </Typography>
                    <List dense disablePadding>
                      {topics.map((row) => (
                        <ListItem key={row.id || row.contentId} disablePadding>
                          <ListItemButton onClick={() => setSelectedTopic(row)}>
                            <ListItemText primary={row.title} secondary={row.bodyText?.slice(0, 120)} />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                ) : null}
              </Stack>
            ) : showingPageMenu ? (
              <List dense disablePadding>
                {pageTopics.map((row) => (
                  <ListItem key={row.id || row.contentId} disablePadding>
                    <ListItemButton onClick={() => setSelectedTopic(row)}>
                      <ListItemText primary={row.title} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Stack spacing={1.5}>
                {displayTopics.map((row) => (
                  <HelpResultCard
                    key={row.id || row.contentId}
                    row={row}
                    onSelectTopic={setSelectedTopic}
                  />
                ))}
              </Stack>
            )}

            {selectedTopic ? (
              <Button size="small" onClick={() => setSelectedTopic(null)}>
                Back to topic list
              </Button>
            ) : null}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default HelpChatModal;
